import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Lock, ShieldCheck } from 'lucide-react'
import { MIN_PASSWORD_LENGTH } from '@shared/constants'
import { AuthNotice, fieldErrorsFrom } from '@/components/auth/AuthLayout'
import PasswordField, { passwordStrength } from '@/components/auth/PasswordField'
import { Button, Modal, useToast } from '@/components/ui'
import { isApiError } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'

export interface ChangePasswordDialogProps {
  open: boolean
  onClose: () => void
}

export default function ChangePasswordDialog({
  open,
  onClose
}: ChangePasswordDialogProps): React.ReactElement {
  const { toast } = useToast()
  const user = useAuthStore((state) => state.user)
  const changePassword = useAuthStore((state) => state.changePassword)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const formRef = useRef<HTMLFormElement>(null)

  // Reopening must never show the previous attempt's values or errors.
  useEffect(() => {
    if (!open) return
    setCurrent('')
    setNext('')
    setConfirm('')
    setSubmitting(false)
    setFormError(null)
    setFieldErrors({})
  }, [open])

  const mismatch = confirm.length > 0 && confirm !== next

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!current) errors.currentPassword = 'Enter your current password.'
    if (!passwordStrength(next).meetsMinimum) {
      errors.newPassword = `Use at least ${MIN_PASSWORD_LENGTH} characters.`
    } else if (next === current) {
      errors.newPassword = 'Choose a password different from your current one.'
    }
    if (confirm !== next) errors.confirm = 'Passwords do not match.'

    return errors
  }

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (submitting) return

    setFormError(null)
    const invalid = validate()
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    setSubmitting(true)
    try {
      await changePassword(current, next)
      toast({
        title: 'Password updated',
        description: 'Every other session on your account has been signed out.',
        variant: 'success'
      })
      onClose()
    } catch (error) {
      if (isApiError(error)) {
        const details = fieldErrorsFrom(error.details)
        if (Object.keys(details).length > 0) {
          setFieldErrors(details)
        } else if (error.status === 400) {
          // The only unvalidated 400 on this route is a wrong current password;
          // a policy rejection names the new one in its message.
          const field = /new password/i.test(error.message) ? 'newPassword' : 'currentPassword'
          setFieldErrors({ [field]: error.message })
        } else {
          setFormError(error.message)
        }
      } else {
        setFormError('Something went wrong. Please try again.')
      }
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Change password"
      description="Saving a new password signs out every other session on your account."
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={submitting}
            iconLeft={<ShieldCheck />}
            onClick={() => formRef.current?.requestSubmit()}
          >
            Update password
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        noValidate
        className="space-y-4"
        onSubmit={(event) => {
          void submit(event)
        }}
      >
        {formError ? (
          <AuthNotice>
            <p>{formError}</p>
          </AuthNotice>
        ) : null}

        {user?.mustChangePassword ? (
          <AuthNotice tone="info" title="A new password is required">
            <p>Your password was reset by an administrator. Choose your own before continuing.</p>
          </AuthNotice>
        ) : null}

        <PasswordField
          label="Current password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          autoComplete="current-password"
          autoFocus
          leadingIcon={<Lock />}
          error={fieldErrors.currentPassword}
          disabled={submitting}
          required
        />

        <PasswordField
          label="New password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
          leadingIcon={<Lock />}
          showStrength
          error={fieldErrors.newPassword}
          disabled={submitting}
          required
        />

        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          leadingIcon={<Lock />}
          error={mismatch ? 'Passwords do not match.' : fieldErrors.confirm}
          disabled={submitting}
          required
        />
      </form>
    </Modal>
  )
}

export { ChangePasswordDialog }
